/**
 * Quick-buy de-dupe guard.
 *
 * A `<quick-buy-drawer>` registers a document-level delegated listener for
 * `[aria-controls="{id}"]` (assets/theme.js). If two drawers share the same id, one click
 * on a matching trigger calls show() on BOTH → the quick-add popup appears twice, stacked.
 *
 * Same-id duplicates arise two ways:
 *   1. Static, at first paint — e.g. a Featured Collection with tabs renders every tab panel
 *      into the DOM at once, so a product that appears in more than one tab produces multiple
 *      cards with the same quick-buy id. (The primary fix threads a per-tab instance_id into the
 *      card ids; this guard is the safety net.)
 *   2. Dynamic — a container re-render (cart drawer inner swap, collection/search facet update)
 *      re-emits a same-id drawer while an already-open one has been relocated to <body>.
 *
 * theme.js is a build output, so instead of patching it we collapse same-id duplicates here:
 * keep an OPEN instance if there is one (never destroy a modal the visitor is using), otherwise
 * keep the first, and remove the rest. HTML ids must be unique, so collapsing is always safe.
 */
(function () {
  if (window.__cartUpsellDedupeReady) return;
  window.__cartUpsellDedupeReady = true;

  var busy = false;

  function dedupeAll() {
    if (busy) return;
    busy = true;

    try {
      var all = document.querySelectorAll('quick-buy-drawer[id]');
      if (all.length < 2) return;

      var byId = {};
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        (byId[el.id] = byId[el.id] || []).push(el);
      }

      Object.keys(byId).forEach(function (id) {
        var list = byId[id];
        if (list.length < 2) return;

        // Prefer keeping an open instance; otherwise the first in document order.
        var keeper = null;
        for (var j = 0; j < list.length; j++) {
          if (list[j].hasAttribute('open')) {
            keeper = list[j];
            break;
          }
        }
        if (!keeper) keeper = list[0];

        list.forEach(function (node) {
          // Never remove the keeper, and never remove an open modal the visitor is interacting with.
          if (node !== keeper && !node.hasAttribute('open')) {
            node.remove();
          }
        });
      });
    } finally {
      busy = false;
    }
  }

  // Initial pass once the DOM is parsed (covers static duplicates like featured-collection tabs).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dedupeAll, { once: true });
  } else {
    dedupeAll();
  }

  // After re-renders that can orphan an open drawer and re-emit a same-id twin. Defer one frame
  // so the re-render has finished before we sweep.
  ['cart:change', 'cart:refresh', 'facet:update'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      requestAnimationFrame(dedupeAll);
    });
  });

  // The cart drawer swaps its inner HTML without a reliable event hook (and defers the swap on
  // some cart changes), so keep a scoped observer on it.
  var drawer = document.getElementById('cart-drawer');
  if (drawer && typeof MutationObserver !== 'undefined') {
    new MutationObserver(function () {
      dedupeAll();
    }).observe(drawer, { childList: true, subtree: true });
  }
})();
